import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./HackathonApplication.css";

const HackathonApplication = ({ user }) => {
    const { eventId } = useParams();
    const navigate = useNavigate();

    const [event, setEvent] = useState(null);
    const [step, setStep] = useState(1);

    const [team, setTeam] = useState({
        name: "",
        school: "",
        description: ""
    });

    const [members, setMembers] = useState([]);

    const [email, setEmail] = useState("");

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        loadEvent();
    }, [eventId]);

    const loadEvent = async () => {
        try {
            const response = await fetch(
                `/api/events/${eventId}`,
                {
                    credentials: "include"
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(
                    data.message || "Unable to load event"
                );
            }

            setEvent(data.event);

            await loadApplication();

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadApplication = async () => {
        try {
            const response = await fetch(
                `/api/events/${eventId}/application`,
                {
                    credentials: "include"
                }
            );

            const data = await response.json();

            if (data.registered) {
                setTeam(data.team);
                setMembers(data.members || []);
                setStep(3);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const createTeam = async () => {
        setError("");
        setMessage("");

        if (!team.name.trim()) {
            setError("Team name is required.");
            return false;
        }

        setSaving(true);

        try {
            const response = await fetch(
                `/api/events/${eventId}/teams`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(team)
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(
                    data.message || "Unable to create team"
                );
            }

            setTeam(data.team);

            setMembers([
                {
                    user_id: user.id,
                    name:
                        user.name ||
                        user.username ||
                        "You",
                    email: user.email,
                    role: "captain"
                }
            ]);

            setMessage("Team created successfully.");

            return true;

        } catch (err) {
            setError(err.message);
            return false;

        } finally {
            setSaving(false);
        }
    };

    const handleContinueFromTeam = async () => {
        const success = await createTeam();

        if (success) {
            setStep(2);
        }
    };

    const addMember = async () => {
        setError("");
        setMessage("");

        if (!email.trim()) {
            setError("Enter a team member's email.");
            return;
        }

        setSaving(true);

        try {
            const response = await fetch(
                `/api/events/${eventId}/teams/${team.id}/members`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        email: email.trim()
                    })
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(
                    data.message || "Unable to add member"
                );
            }

            setMembers(prev => [
                ...prev,
                data.member
            ]);

            setEmail("");

            setMessage("Team member added.");

        } catch (err) {
            setError(err.message);

        } finally {
            setSaving(false);
        }
    };

    const removeMember = async (userId) => {
        setError("");
        setMessage("");

        try {
            const response = await fetch(
                `/api/events/${eventId}/teams/${team.id}/members/${userId}`,
                {
                    method: "DELETE",
                    credentials: "include"
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(
                    data.message || "Unable to remove member"
                );
            }

            setMembers(prev =>
                prev.filter(
                    member => member.user_id !== userId
                )
            );

        } catch (err) {
            setError(err.message);
        }
    };

   const submitApplication = async () => {
    setError("");
    setMessage("");
    setSaving(true);

    try {
        const response = await fetch(
            `/api/events/${eventId}/teams/${team.id}/submit`,
            {
                method: "POST",
                credentials: "include"
            }
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(
                data.message || "Unable to submit application"
            );
        }

        setTeam(prev => ({
            ...prev,
            status: "submitted"
        }));

        setMessage(
            "Your team application has been submitted successfully."
        );

    } catch (err) {
        setError(err.message);
    } finally {
        setSaving(false);
    }
};

    if (loading) {
        return (
            <div className="hackathon-page">
                <div className="application-card">
                    Loading...
                </div>
            </div>
        );
    }

    if (error && !event) {
        return (
            <div className="hackathon-page">
                <div className="application-card error-card">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="hackathon-page">

            <div className="application-container">

                <div className="event-header">

                    <div>
                        <div className="event-label">
                            NASA ASTROBEE
                        </div>

                        <h1>
                            {event?.name}
                        </h1>

                        <p>
                            {event?.description}
                        </p>
                    </div>

                    <div className="event-status">
                        {event?.status}
                    </div>

                </div>

                <div className="steps">

                    <div
                        className={
                            `step ${step >= 1 ? "active" : ""}`
                        }
                    >
                        <span>1</span>
                        Team
                    </div>

                    <div
                        className={
                            `step ${step >= 2 ? "active" : ""}`
                        }
                    >
                        <span>2</span>
                        Members
                    </div>

                    <div
                        className={
                            `step ${step >= 3 ? "active" : ""}`
                        }
                    >
                        <span>3</span>
                        Review
                    </div>

                </div>

                {message && (
                    <div className="success-message">
                        {message}
                    </div>
                )}

                {error && (
                    <div className="error-message">
                        {error}
                    </div>
                )}

                {/* STEP 1 */}

                {step === 1 && (
                    <div className="application-card">

                        <h2>Register Your Team</h2>

                        <p className="section-description">
                            Create a team for the NASA Astrobee
                            Hackathon 2026.
                        </p>

                        <label>
                            Team Name
                        </label>

                        <input
                            type="text"
                            value={team.name}
                            onChange={e =>
                                setTeam({
                                    ...team,
                                    name: e.target.value
                                })
                            }
                            placeholder="Enter your team name"
                        />

                        <label>
                            School / University
                        </label>

                        <input
                            type="text"
                            value={team.school}
                            onChange={e =>
                                setTeam({
                                    ...team,
                                    school: e.target.value
                                })
                            }
                            placeholder="Enter school or university"
                        />

                        <label>
                            Team Description
                        </label>

                        <textarea
                            value={team.description}
                            onChange={e =>
                                setTeam({
                                    ...team,
                                    description: e.target.value
                                })
                            }
                            placeholder="Tell us briefly about your team"
                            rows="4"
                        />

                        <div className="captain-box">

                            <div className="captain-title">
                                Team Captain
                            </div>

                            <strong>
                                {user?.name ||
                                    user?.username}
                            </strong>

                            <span>
                                {user?.email}
                            </span>

                        </div>

                        <div className="button-row">

                            <button
                                className="secondary-button"
                                onClick={() =>
                                    navigate(
                                        `/events/${eventId}`
                                    )
                                }
                            >
                                Cancel
                            </button>

                            <button
                                className="primary-button"
                                onClick={
                                    handleContinueFromTeam
                                }
                                disabled={saving}
                            >
                                {saving
                                    ? "Creating..."
                                    : "Continue →"}
                            </button>

                        </div>

                    </div>
                )}

                {/* STEP 2 */}

                {step === 2 && (
                    <div className="application-card">

                        <h2>Team Members</h2>

                        <p className="section-description">
                            Add students who will participate
                            with your team.
                        </p>

                        <div className="team-summary">

                            <strong>
                                {team.name}
                            </strong>

                            <span>
                                {members.length} / 4 members
                            </span>

                        </div>

                        <div className="member-list">

                            {members.map(member => (

                                <div
                                    className="member-row"
                                    key={member.user_id}
                                >

                                    <div className="member-info">

                                        <strong>
                                            {member.name}
                                        </strong>

                                        <span>
                                            {member.email}
                                        </span>

                                    </div>

                                    <div className="member-actions">

                                        <span className="role">
                                            {member.role}
                                        </span>

                                        {member.role !==
                                            "captain" && (
                                            <button
                                                onClick={() =>
                                                    removeMember(
                                                        member.user_id
                                                    )
                                                }
                                            >
                                                Remove
                                            </button>
                                        )}

                                    </div>

                                </div>

                            ))}

                        </div>

                        {members.length < 4 && (

                            <div className="add-member">

                                <label>
                                    Member Email
                                </label>

                                <div className="add-member-row">

                                    <input
                                        type="email"
                                        value={email}
                                        onChange={e =>
                                            setEmail(
                                                e.target.value
                                            )
                                        }
                                        placeholder="student@example.com"
                                    />

                                    <button
                                        className="primary-button"
                                        onClick={addMember}
                                        disabled={saving}
                                    >
                                        Add
                                    </button>

                                </div>

                                <small>
                                    The student must already
                                    have a STEMX365 account.
                                </small>

                            </div>

                        )}

                        <div className="button-row">

                            <button
                                className="secondary-button"
                                onClick={() => setStep(1)}
                            >
                                ← Back
                            </button>

                            <button
                                className="primary-button"
                                onClick={() => setStep(3)}
                            >
                                Review →
                            </button>

                        </div>

                    </div>
                )}

                {/* STEP 3 */}

                {step === 3 && (
                    <div className="application-card">

                        <h2>Review Application</h2>

                        <p className="section-description">
                            Review your team information before
                            submitting the application.
                        </p>

                        <div className="review-section">

                            <div className="review-label">
                                Event
                            </div>

                            <div className="review-value">
                                {event?.name}
                            </div>

                        </div>

                        <div className="review-section">

                            <div className="review-label">
                                Team
                            </div>

                            <div className="review-value">
                                {team.name}
                            </div>

                        </div>

                        <div className="review-section">

                            <div className="review-label">
                                School / University
                            </div>

                            <div className="review-value">
                                {team.school || "—"}
                            </div>

                        </div>

                        <div className="review-section">

                            <div className="review-label">
                                Team Members
                            </div>

                            <div className="review-members">

                                {members.map(member => (

                                    <div
                                        key={member.user_id}
                                        className="review-member"
                                    >
                                        <strong>
                                            {member.name}
                                        </strong>

                                        <span>
                                            {member.email}
                                        </span>

                                        <em>
                                            {member.role}
                                        </em>
                                    </div>

                                ))}

                            </div>

                        </div>

                        <div className="application-note">

                            <strong>
                                Application Status
                            </strong>

                            <span>
                                Pending organizer approval
                            </span>

                        </div>

                        <div className="button-row">

                            <button
                                className="secondary-button"
                                onClick={() => setStep(2)}
                            >
                                ← Back
                            </button>

                            <button
                                className="primary-button"
                                onClick={submitApplication}
                            >
                                Submit Application
                            </button>

                        </div>

                    </div>
                )}

            </div>

        </div>
    );
};

export default HackathonApplication;